/**
 * Snapshot helpers for save/load backtest.
 * Always use the store's unexpanded expressionGraph (never expandExpressionGraph) so
 * series-aware / series-aware-window restore as compact UI.
 * For backend (e.g. tracking warmup), we also include expanded graphs so constant/seriesAware
 * nodes are resolved to literal/ohlc_series and validation succeeds.
 */
import { useAppStore } from '../state/store';
import type { BacktestSnapshot } from '../state/types';
import { expandExpressionGraph } from './expandExpressionGraph';

/**
 * Build a snapshot from current store state (unexpanded expressionGraph).
 * Also includes expressionGraphExpanded and exitExpressionGraphExpanded for backend use.
 * Caller provides name; groupLabel and id are optional. updatedAt is set to now.
 */
export function getBacktestSnapshotForSave(
  name: string,
  groupLabel?: string,
  id?: string
): BacktestSnapshot {
  const state = useAppStore.getState();
  const { expressionGraph, exitExpressionGraph, constants } = state;
  const expressionGraphExpanded =
    expressionGraph?.rootId != null && expressionGraph.rootId !== ''
      ? expandExpressionGraph(expressionGraph, constants)
      : undefined;
  const exitExpressionGraphExpanded =
    exitExpressionGraph?.rootId != null && exitExpressionGraph.rootId !== ''
      ? expandExpressionGraph(exitExpressionGraph, constants)
      : undefined;
  const backtestSettings =
    state.backtestSettings.backtestType === 'simple'
      ? { ...state.backtestSettings, simpleTradeSet: state.selectedTradeSetKey }
      : state.backtestSettings;
  return {
    instruments: state.instruments,
    primaryInstrumentId: state.primaryInstrumentId,
    indicators: state.indicators,
    expressionGraph: state.expressionGraph,
    exitExpressionGraph: state.exitExpressionGraph,
    constants: state.constants,
    backtestSettings,
    name,
    ...(groupLabel !== undefined && groupLabel !== '' && { groupLabel }),
    updatedAt: new Date().toISOString(),
    ...(id !== undefined && id !== '' && { id }),
    ...(expressionGraphExpanded != null && { expressionGraphExpanded }),
    ...(exitExpressionGraphExpanded != null && { exitExpressionGraphExpanded }),
  };
}

/**
 * Hydrate the store from a loaded snapshot. Only updates the slice that the snapshot contains;
 * also sets current-saved backtest to this snapshot's id/name.
 * Clears backtest result/summary/equity so the chart does not show stale trade markers from
 * a previous run (different symbol/range); user must run backtest again to see markers.
 */
export function loadBacktestSnapshot(snapshot: BacktestSnapshot): void {
  const set = useAppStore.setState;
  set({
    instruments: snapshot.instruments,
    primaryInstrumentId: snapshot.primaryInstrumentId,
    indicators: snapshot.indicators,
    expressionGraph: snapshot.expressionGraph,
    exitExpressionGraph: snapshot.exitExpressionGraph ?? { rootId: null, expressions: {} },
    constants: snapshot.constants,
    backtestSettings: snapshot.backtestSettings,
    selectedTradeSetKey: snapshot.backtestSettings?.simpleTradeSet ?? 'exit',
    currentSavedBacktestId: snapshot.id ?? null,
    currentSavedBacktestName: snapshot.name,
    currentSavedBacktestGroupLabel: snapshot.groupLabel ?? null,
    backtestSummary: null,
    backtestResult: null,
    backtestEquityCurve: null,
  });
}
